// src/services/joinService.js
//
// SourceX Roadmap PHASE 2 — client side of the SoA x eCRF join.
//
// Mirrors ecrfExtractionService.js / protocolExtractionService.js: the same
// private callFn() shape, the same tenant/site body, the same listen helpers.
//
// ---------------------------------------------------------------------------
// WHY THE MERGE HAPPENS HERE AND NOT IN THE CLOUD FUNCTION
// ---------------------------------------------------------------------------
// computeStudyJoin is deliberately side-effect free: it returns a freshly
// computed table and writes nothing. This module merges that into the stored
// one with mergeJoinDecisions() and saves the result.
//
// That merge is the piece that keeps a coordinator's confirmations alive across
// re-computation (roadmap §5 rule 2), and it is the subtlest logic in Phase 2 —
// confirmed beats a recompute, rejection is sticky, a decision dies only when
// its anchor form dies. It exists in exactly ONE place, src/services/joinTypes.js,
// with 41 tests. CRA cannot import from functions/, so putting it server-side
// too would mean two copies of the one function whose entire job is to not lose
// human decisions, drifting apart.

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { getEcrfExtractionDocPath, getStudyJoinDocPath } from "./firestorePaths";
import { getActiveSiteId, getActiveTenantId } from "./activeTenant";
import {
  MATCH_METHODS,
  MATCH_STATUS,
  emptyJoinTable,
  makeJoinMapping,
  mappingKey,
  mergeJoinDecisions,
} from "./joinTypes";

const FN_BASE = FUNCTIONS_BASE;

const callFn = async (name, body) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();

  const res = await fetch(`${FN_BASE}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      tenantId: getActiveTenantId(),
      siteId: getActiveSiteId(),
      ...body,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `${name} failed (${res.status}).`);
  return data;
};

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export const getJoin = async (studyId) => {
  const snap = await getDoc(doc(db, getStudyJoinDocPath(studyId)));
  return snap.exists() ? snap.data() : null;
};

export const listenJoin = (studyId, cb) =>
  onSnapshot(
    doc(db, getStudyJoinDocPath(studyId)),
    (snap) => cb(snap.exists() ? snap.data() : null),
    (err) => {
      console.warn("[join] listen failed:", err && err.message);
      cb(null);
    }
  );

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

const saveTable = async (studyId, table) => {
  await setDoc(
    doc(db, getStudyJoinDocPath(studyId)),
    { ...table, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || "" },
    { merge: false } // the table is authoritative as a whole; a partial merge
    // could leave a mapping the merge just decided to drop.
  );
};

/**
 * Recompute the join and fold it into whatever the coordinator has already
 * decided. Returns the merged table (already saved).
 *
 * `anchors` are passed so a mapping whose form no longer exists in the current
 * eCRF is dropped rather than left dangling for Phase 3 to assemble against.
 */
export const recomputeJoin = async (studyId, opts = {}) => {
  const { computed } = await callFn("computeStudyJoin", {
    studyId,
    protocolId: opts.protocolId,
    ecrfId: opts.ecrfId,
    useAi: opts.useAi !== false,
  });

  const previous = await getJoin(studyId);
  const formIds = new Set((computed.mappings || []).map((m) => m.form_id));
  // Every SoA row the recompute saw, matched or not — a confirmed mapping for a
  // row that has vanished from the protocol is dangling.
  const soaKeys = new Set([
    ...(computed.mappings || []).map((m) => m.soa_key),
    ...(computed.unmatched || []).map((u) => u.soa_key),
  ]);

  const merged = mergeJoinDecisions(previous, computed, { formIds, soaKeys });
  await saveTable(studyId, merged);
  return merged;
};

/** Confirm or reject one mapping. */
export const setMappingStatus = async (studyId, key, status) => {
  const table = (await getJoin(studyId)) || emptyJoinTable();
  const mappings = (table.mappings || []).map((m) =>
    m.key === key
      ? {
          ...m,
          status,
          reviewedBy: auth.currentUser?.email || "",
          // Date.now() rather than serverTimestamp(): this sits inside an array,
          // and Firestore sentinels are not allowed in array elements.
          reviewedAt: Date.now(),
        }
      : m
  );
  await saveTable(studyId, { ...table, mappings });
  return mappings;
};

/**
 * Add a mapping a human authored. Marked MANUAL and CONFIRMED, which together
 * mean mergeJoinDecisions will never drop it just because the matcher does not
 * reproduce it — a human added it precisely because the matcher missed it.
 */
export const addManualMapping = async (studyId, { soaKey, soaName, formId, formName }) => {
  const table = (await getJoin(studyId)) || emptyJoinTable();
  const key = mappingKey(soaKey, formId);
  if ((table.mappings || []).some((m) => m.key === key)) return table.mappings;

  const mapping = makeJoinMapping({
    soa_key: soaKey,
    soa_name: soaName,
    form_id: formId,
    form_name: formName,
    method: MATCH_METHODS.MANUAL,
    status: MATCH_STATUS.CONFIRMED,
    score: 1,
    rationale: `Added by ${auth.currentUser?.email || "a reviewer"}.`,
    reviewedBy: auth.currentUser?.email || "",
    reviewedAt: Date.now(),
  });

  const mappings = [...(table.mappings || []), mapping];
  // The row is answered now, so it should stop appearing as unmatched.
  const unmatched = (table.unmatched || []).filter((u) => u.soa_key !== soaKey);
  await saveTable(studyId, { ...table, mappings, unmatched });
  return mappings;
};

/**
 * Assemble the per-visit source documents from the saved, reviewed join
 * (Phase 3). Pure server-side code, no AI. Writes a SEPARATE draft template at
 * sourcex_templates/{studyId}__assembled so a hand-built template is never
 * clobbered, and returns its id for the caller to open in SectionEditor.
 */
export const assembleVisits = async (studyId, opts = {}) => {
  const data = await callFn("assembleStudyVisits", {
    studyId,
    onlyConfirmed: opts.onlyConfirmed === true,
  });
  return data; // { templateId, stats, warningCount }
};

/** Confirm every still-unreviewed proposal in one go. */
export const confirmAllProposed = async (studyId) => {
  const table = (await getJoin(studyId)) || emptyJoinTable();
  const email = auth.currentUser?.email || "";
  const now = Date.now();
  const mappings = (table.mappings || []).map((m) =>
    m.status === MATCH_STATUS.PROPOSED
      ? { ...m, status: MATCH_STATUS.CONFIRMED, reviewedBy: email, reviewedAt: now }
      : m
  );
  await saveTable(studyId, { ...table, mappings });
  return mappings;
};

/**
 * The eCRF's form list, for the "pick a different form" control. Read straight
 * from the stored extraction — no PDF, no model (roadmap §5 rule 1).
 */
export const getEcrfFormOptions = async (studyId, ecrfId) => {
  if (!ecrfId) return [];
  const snap = await getDoc(doc(db, getEcrfExtractionDocPath(studyId, ecrfId)));
  if (!snap.exists()) return [];
  return (snap.data().forms || [])
    .map((f) => ({ form_id: f.form_id, name: f.name, fieldCount: (f.fields || []).length }))
    .sort((a, b) => a.name.localeCompare(b.name));
};
